import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  Icon,
  IconButton,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import Fuse from 'fuse.js';
import { Helmet } from 'react-helmet-async';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { DocumentationChapter, DocumentationHeading } from 'api/documentation';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';

import { ApiReferenceNav } from './ApiReference/ApiReferenceNav';
import { ApiReferenceView } from './ApiReference/ApiReferenceView';
import { useApiReference } from './ApiReference/useApiReference';
import { API_REFERENCE_PATH, parseApiRoute } from './ApiReference/utils';
import { useDocsPanel } from './DocsPanel/context';
import { primaryAppRoute } from './DocsPanel/utils';
import {
  DocumentationFeedback,
  useDocumentationFeedback,
} from './DocumentationFeedback';
import { DocumentationFollowAlongButton } from './DocumentationFollowAlongButton';
import { DocumentationAssetProvider } from './DocumentationImage';
import { DocumentationMarkdown } from './DocumentationMarkdown';
import {
  DocumentationSectionLinks,
  DocumentationToc,
  tocHeadings,
  useActiveHeading,
  useAppHeaderHeight,
} from './DocumentationToc';
import { useProductDocumentation } from './useProductDocumentation';
import {
  buildAnchorMap,
  buildDocumentationSections,
  buildDocumentationTree,
  chapterLineage,
  chapterPath,
  chapterUrl,
  DOCUMENTATION_FONT_FAMILY,
  DocumentationTreeNode,
  findDocumentationChapter,
  formatReviewedDate,
  groupDocumentationTree,
  keepSearch,
  scrollToHeading,
} from './utils';

const NO_HEADINGS: DocumentationHeading[] = [];

export const Documentation: React.FC = () => {
  // Shared with the follow-along panel, so the docs download once.
  const { documentation, error } = useProductDocumentation();
  const docsPanel = useDocsPanel();
  const [search, setSearch] = useState('');
  // Sections opened or closed by hand; the active page's ancestors stay open.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const navigateTo = useNavigate();
  // Docs links keep the app's query; see keepSearch.
  const navigate = (to: string, options?: { replace?: boolean }) =>
    navigateTo(keepSearch(to, location.search), options);
  const pageTop = useRef<HTMLDivElement>(null);

  const chapters = useMemo(
    () =>
      [...(documentation?.chapters ?? [])].sort(
        (left, right) => left.order - right.order,
      ),
    [documentation],
  );
  const sections = useMemo(() => buildDocumentationSections(chapters), [
    chapters,
  ]);
  const anchorMap = useMemo(() => buildAnchorMap(sections), [sections]);
  const tree = useMemo(() => buildDocumentationTree(chapters), [chapters]);
  const navGroups = useMemo(() => groupDocumentationTree(tree), [tree]);
  // API reference pages (/documentation/api-reference/<service>/...) are
  // generated from the services' specs and shown under the API Reference page.
  const apiRoute = useMemo(() => parseApiRoute(location.pathname), [
    location.pathname,
  ]);
  const requestedChapter = useMemo(
    () =>
      apiRoute
        ? chapters.find(chapter => chapterPath(chapter) === API_REFERENCE_PATH)
        : findDocumentationChapter(chapters, location.pathname),
    [apiRoute, chapters, location.pathname],
  );
  const searchEngine = useMemo(
    () =>
      new Fuse(sections, {
        keys: ['heading', 'chapterTitle', 'body'],
        threshold: 0.35,
        ignoreLocation: true,
      }),
    [sections],
  );
  const searchResults = useMemo(
    () =>
      search.trim() ? searchEngine.search(search.trim()).slice(0, 12) : [],
    [search, searchEngine],
  );

  const activeChapter: DocumentationChapter | undefined =
    requestedChapter ?? chapters[0];
  const activeIndex = chapters.findIndex(
    chapter => chapter.slug === activeChapter?.slug,
  );
  const activeLineage = useMemo(() => chapterLineage(chapters, activeChapter), [
    activeChapter,
    chapters,
  ]);

  // Load the service specs only while the API reference is open.
  const apiServices = useApiReference(
    !!activeChapter && chapterPath(activeChapter) === API_REFERENCE_PATH,
  );

  const headerHeight = useAppHeaderHeight();
  const [activeHeadingId, setActiveHeadingId] = useActiveHeading(
    activeChapter?.headings ?? NO_HEADINGS,
    headerHeight,
  );
  // Pages can turn off feedback and Follow along in their front matter.
  const showFeedback = activeChapter?.feedback !== false;
  const showFollowAlong = activeChapter?.follow_along !== false;
  const feedback = useDocumentationFeedback(
    showFeedback ? activeChapter : undefined,
    documentation?.revision,
  );

  // Send unknown and legacy URLs to the canonical path of the shown chapter.
  useEffect(() => {
    if (!documentation || !activeChapter) return;
    // Generated API pages have their own URLs under the API Reference page.
    if (apiRoute && requestedChapter) return;
    const canonical = chapterUrl(activeChapter);
    if (decodeURIComponent(location.pathname) === canonical) return;
    const to = requestedChapter ? `${canonical}${location.hash}` : canonical;
    navigateTo(keepSearch(to, location.search), { replace: true });
  }, [
    activeChapter,
    apiRoute,
    documentation,
    location.hash,
    location.pathname,
    location.search,
    navigateTo,
    requestedChapter,
  ]);

  // A new page starts at the top unless the link targets a section. Only a
  // page change does this: losing the hash must not undo a section jump.
  const hashRef = useRef(location.hash);
  hashRef.current = location.hash;
  // Keyed on the path so moving between API endpoints also starts at the top.
  useEffect(() => {
    if (!hashRef.current) pageTop.current?.scrollIntoView({ block: 'start' });
  }, [activeChapter?.slug, location.pathname]);

  useEffect(() => {
    if (!activeChapter || !location.hash) return;
    const anchor = decodeURIComponent(location.hash.slice(1));
    window.requestAnimationFrame(() => scrollToHeading(anchor));
  }, [activeChapter, location.hash]);

  const openChapter = (chapter?: DocumentationChapter, anchor?: string) => {
    if (!chapter) return;
    setSearch('');
    navigate(chapterUrl(chapter, anchor));
  };

  const selectHeading = (anchor: string) => {
    if (!activeChapter) return;
    setActiveHeadingId(anchor);
    // Scroll here too: re-selecting the current hash changes no URL.
    scrollToHeading(anchor);
    navigate(chapterUrl(activeChapter, anchor), { replace: true });
  };

  const renderNavNode = ({ chapter, children }: DocumentationTreeNode) => {
    const path = chapterPath(chapter);
    const isApiReference = path === API_REFERENCE_PATH;
    const isOpenPage = path === chapterPath(activeChapter!);
    // On an endpoint page the endpoint, not the API Reference page, is current.
    const isActive = isOpenPage && !apiRoute;
    // A page without sub-pages lists its own sections while it is open.
    const sections =
      isActive && !children.length ? tocHeadings(chapter.headings ?? []) : [];
    const isOpen = toggled[path] ?? activeLineage.has(path);
    return (
      <Box key={path}>
        <Flex
          align="center"
          borderRadius={6}
          transition="background 150ms"
          bg={isActive ? 'selectedSideNavMenuBg' : 'transparent'}
          color={isActive ? 'primary' : customTheme.colors.gray['300']}
          _hover={{ color: 'primary' }}
        >
          <Button
            variant="unstyled"
            flex={1}
            display="block"
            h="auto"
            py={1.5}
            pl={3}
            pr={children.length ? 1 : 3}
            fontSize="15px"
            fontWeight={400}
            lineHeight={1.45}
            whiteSpace="normal"
            textAlign="left"
            color="inherit"
            aria-current={isActive ? 'page' : undefined}
            onClick={() => {
              setToggled(state => ({ ...state, [path]: true }));
              openChapter(chapter);
            }}
          >
            {chapter.title}
          </Button>
          {children.length > 0 && (
            <IconButton
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${chapter.title}`}
              variant="unstyled"
              size="xs"
              minW={7}
              color="gray.400"
              _hover={{ color: 'primary' }}
              icon={
                <Icon
                  as={FiChevronRight}
                  transition="transform 150ms"
                  transform={isOpen ? 'rotate(90deg)' : undefined}
                />
              }
              onClick={() =>
                setToggled(state => ({ ...state, [path]: !isOpen }))
              }
            />
          )}
        </Flex>
        {children.length > 0 && (
          <Collapse in={isOpen} animateOpacity>
            <VStack
              align="stretch"
              spacing={0.5}
              mt={0.5}
              ml={3}
              pl={2}
              borderLeft="1px solid"
              borderColor="gray.100"
            >
              {children.map(renderNavNode)}
            </VStack>
          </Collapse>
        )}
        {sections.length > 0 && (
          <Box mt={1} mb={2} ml={3}>
            <DocumentationSectionLinks
              headings={chapter.headings ?? []}
              activeId={activeHeadingId}
              onSelect={selectHeading}
            />
          </Box>
        )}
        {isApiReference && isOpenPage && (
          <ApiReferenceNav
            services={apiServices}
            activeRoute={apiRoute}
            onOpen={url => {
              setSearch('');
              navigate(url);
            }}
          />
        )}
      </Box>
    );
  };

  if (error) {
    return (
      <Box p={{ base: 4, md: 8 }}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Product documentation is temporarily unavailable. Refresh the page
            or try again later.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (!documentation || !activeChapter) {
    return (
      <Stack p={{ base: 4, md: 8 }} spacing={4}>
        <Skeleton height="42px" />
        <Skeleton height="280px" />
      </Stack>
    );
  }

  const feedbackProps = {
    chapter: activeChapter,
    answers: feedback.answers,
    onSave: feedback.save,
    activeSectionId: activeHeadingId,
  };
  const previousChapter = chapters[activeIndex - 1];
  const nextChapter = chapters[activeIndex + 1];
  const topLevelChapter = chapters.find(
    chapter =>
      !chapter.parent &&
      activeLineage.has(chapterPath(chapter)) &&
      chapter.slug !== activeChapter.slug,
  );
  const breadcrumb = [
    chapters.find(chapter => activeLineage.has(chapterPath(chapter)))?.group,
    topLevelChapter?.title,
  ].filter(Boolean);

  const pagerCard = (
    chapter: DocumentationChapter | undefined,
    direction: 'Previous' | 'Next',
  ) =>
    chapter ? (
      <Button
        variant="unstyled"
        display="flex"
        h="auto"
        px={4}
        py={3}
        gap={3}
        alignItems="center"
        flexDirection={direction === 'Next' ? 'row-reverse' : 'row'}
        textAlign={direction === 'Next' ? 'right' : 'left'}
        fontWeight={400}
        border="1px solid"
        borderColor="gray.200"
        borderRadius={8}
        transition="border-color 150ms, color 150ms"
        _hover={{ borderColor: 'primary', color: 'primary' }}
        onClick={() => openChapter(chapter)}
      >
        <Icon
          as={direction === 'Next' ? FiChevronRight : FiChevronLeft}
          color="gray.400"
        />
        <Box flex={1} minW={0}>
          <Text fontSize="12px" color="gray.500">
            {direction}
          </Text>
          <Text fontSize="15px" whiteSpace="normal" noOfLines={2}>
            {chapter.title}
          </Text>
        </Box>
      </Button>
    ) : (
      <Box />
    );

  return (
    <Box
      ref={pageTop}
      fontFamily={DOCUMENTATION_FONT_FAMILY}
      minH="calc(100vh - 90px)"
      m={{ base: 3, lg: 4 }}
      bg="white"
      borderRadius={6}
      scrollMarginTop="var(--docs-header-height, 0px)"
      sx={{ '--docs-header-height': `${headerHeight}px` }}
    >
      {/* Nav, article and outline stay together as one centred block. */}
      <Flex
        maxW="1440px"
        mx="auto"
        align="flex-start"
        direction={{ base: 'column', lg: 'row' }}
      >
        <Helmet>
          <title>{activeChapter.title} - Product Documentation</title>
        </Helmet>
        <Box
          as="aside"
          w={{ base: 'full', lg: '290px' }}
          minW={{ lg: '290px' }}
          px={4}
          pt={6}
          // Clears the floating Ariksa AI button in the bottom-left corner.
          pb={20}
          position={{ lg: 'sticky' }}
          // Sticks just below the app header instead of sliding under it.
          top={{ lg: 'var(--docs-header-height, 0px)' }}
          maxH={{ lg: 'calc(100vh - var(--docs-header-height, 0px))' }}
          overflowY="auto"
        >
          <Text fontSize="16px" fontWeight={400} color="primary" px={3} mb={4}>
            {documentation.title}
          </Text>

          <Box pb={4}>
            <SearchBox
              aria-label="Search product documentation"
              placeholder="Search documentation"
              value={search}
              onChange={setSearch}
              onClickClose={() => setSearch('')}
              showShortcut={false}
            />
          </Box>
          {searchResults.length ? (
            <VStack align="stretch" spacing={1} mb={5}>
              {searchResults.map(({ item }) => (
                <Button
                  key={`${item.chapterSlug}-${item.anchor}`}
                  variant="ghost"
                  h="auto"
                  py={2}
                  px={3}
                  justifyContent="flex-start"
                  textAlign="left"
                  whiteSpace="normal"
                  onClick={() =>
                    openChapter(
                      chapters.find(
                        chapter => chapter.slug === item.chapterSlug,
                      ),
                      item.anchor,
                    )
                  }
                >
                  <Box>
                    <Text fontSize="sm" fontWeight={400}>
                      {item.heading}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {item.chapterTitle}
                    </Text>
                  </Box>
                </Button>
              ))}
            </VStack>
          ) : search.trim() ? (
            <Text fontSize="sm" color="gray.500" mb={5}>
              No matching documentation found.
            </Text>
          ) : null}
          <Box as="nav" aria-label="Documentation chapters">
            {navGroups.map(({ label, nodes }, index) => (
              <Box key={label ?? `group-${index}`} mt={index ? 6 : 0}>
                {label && (
                  <Text
                    px={3}
                    mb={1.5}
                    fontSize="13px"
                    fontWeight={600}
                    letterSpacing="0.06em"
                    textTransform="uppercase"
                    color="black"
                  >
                    {label}
                  </Text>
                )}
                <VStack align="stretch" spacing={0.5}>
                  {nodes.map(renderNavNode)}
                </VStack>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          as="article"
          flex={1}
          minW={0}
          w="full"
          px={{ base: 5, md: 12 }}
          py={{ base: 6, md: 10 }}
        >
          <Box maxW="780px" mx="auto">
            {apiRoute ? (
              <ApiReferenceView
                route={apiRoute}
                services={apiServices}
                onOpen={navigate}
              />
            ) : (
              <>
                {/* v2 pages carry their title outside the Markdown body. */}
                {activeChapter.headings && (
                  <Box as="header" mb={8}>
                    {breadcrumb.length > 0 && (
                      <Text fontSize="14px" color="primary" mb={2}>
                        {breadcrumb.join(' / ')}
                      </Text>
                    )}
                    <Heading
                      as="h1"
                      fontFamily={DOCUMENTATION_FONT_FAMILY}
                      fontSize="34px"
                      fontWeight={600}
                      color="black"
                    >
                      {activeChapter.title}
                    </Heading>
                    {activeChapter.summary && (
                      <Text
                        mt={3}
                        fontSize="19px"
                        lineHeight={1.5}
                        color="gray.500"
                      >
                        {activeChapter.summary}
                      </Text>
                    )}
                    {/* Docks this page beside the app screen it describes. */}
                    {showFollowAlong && (
                      <DocumentationFollowAlongButton
                        onClick={() =>
                          docsPanel.followAlong(
                            chapterPath(activeChapter),
                            activeHeadingId,
                            primaryAppRoute(activeChapter),
                          )
                        }
                      />
                    )}
                  </Box>
                )}
                <DocumentationAssetProvider>
                  <DocumentationMarkdown
                    key={activeChapter.slug}
                    chapter={activeChapter}
                    anchorMap={anchorMap}
                    // App links open the screen with this guide beside it.
                    onAppLink={href =>
                      docsPanel.followAlong(
                        chapterPath(activeChapter),
                        activeHeadingId,
                        href,
                      )
                    }
                  />
                </DocumentationAssetProvider>
                {showFeedback && (
                  <Box
                    display={{ xl: 'none' }}
                    mt={12}
                    pt={8}
                    borderTop="1px solid"
                    borderColor="gray.100"
                  >
                    <DocumentationFeedback {...feedbackProps} />
                  </Box>
                )}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={12}>
                  {pagerCard(previousChapter, 'Previous')}
                  {pagerCard(nextChapter, 'Next')}
                </SimpleGrid>
                <Text mt={6} fontSize="12px" color="gray.500">
                  {activeChapter.last_reviewed &&
                    `Last reviewed ${formatReviewedDate(
                      activeChapter.last_reviewed,
                    )} · `}
                  Revision {documentation.revision.slice(0, 8)}
                </Text>
              </>
            )}
          </Box>
        </Box>

        <Box
          as="aside"
          display={{ base: 'none', xl: 'block' }}
          w="260px"
          minW="260px"
          px={5}
          py={10}
          position="sticky"
          top="var(--docs-header-height, 0px)"
          maxH="calc(100vh - var(--docs-header-height, 0px))"
          overflowY="auto"
        >
          {/* Endpoint pages have no outline or page feedback. */}
          <VStack align="stretch" spacing={8} hidden={!!apiRoute}>
            <DocumentationToc
              headings={activeChapter.headings ?? []}
              activeId={activeHeadingId}
              onSelect={selectHeading}
            />
            {showFeedback && (
              <DocumentationFeedback {...feedbackProps} compact />
            )}
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};
